import { Controller } from "@hotwired/stimulus"
import { claim, release, mountBar, unmountBar, publish } from "../lib/audio_preview_bus"
import { BusyDelay } from "../lib/busy_delay.mjs"
import { AudioLoadState } from "../lib/audio_load_state.mjs"

// The "teraz gra" bar (#124, criterion 7). It owns the one <audio> element on the page; the
// cards in the grid are remotes that hand it a clip to play. Sticky, so the transport is still
// there after you have scrolled past the card that started it.
//
// Hidden until something plays. `load()` and `stop()` are called by the bus, not by markup -
// this controller's own actions are the two things a person does to the bar itself.
export default class extends Controller {
  static targets = ["audio", "toggle", "playIcon", "pauseIcon", "link", "track", "fill", "time",
                    "status", "statusText", "spinner"]
  static values = {
    clipSeconds: { type: Number, default: 30 },
    playLabel: String,
    pauseLabel: String,
    retryLabel: String,
    loadingLabel: String,
    errorLabel: String,
    timeoutLabel: String,
    autoplayLabel: String
  }

  connect() {
    this.spinnerDelay = new BusyDelay({
      show: () => this.spinnerTarget.classList.remove("hidden"),
      hide: () => this.spinnerTarget.classList.add("hidden")
    })
    this.loadState = new AudioLoadState({
      baseURL: document.baseURI,
      onLoading: () => this.#renderLoading(),
      onReady: () => this.#renderReady(),
      onFailure: (reason) => this.#renderFailure(reason)
    })
    mountBar(this)
  }

  disconnect() {
    unmountBar(this)
    // A Turbo navigation takes the markup away; an element detached from the document plays on
    // perfectly happily if nobody stops it.
    this.audioTarget.pause()
    this.#cancelLoad()
    release(this.audioTarget)
  }

  // Driven by the bus ------------------------------------------------------------------------

  // source: { token, src, name, href }. The same clip asked for twice is resumed rather than
  // reloaded - re-assigning src would drop the buffer and start the fetch again.
  load(source) {
    if (this.token !== source.token) {
      this.#cancelLoad()
      this.audioTarget.pause()
      this.token = source.token
      this.source = source
      this.audioTarget.src = source.src
      this.audioTarget.currentTime = 0
      this.linkTarget.textContent = source.name
      this.linkTarget.href = source.href
      this.element.classList.remove("hidden")
      return this.#beginLoad()
    }

    this.element.classList.remove("hidden")
    if (this.loadFailed) return this.#beginLoad()
    this.#play(this.loadGeneration)
  }

  stop() {
    this.audioTarget.pause()
  }

  // Interaction ------------------------------------------------------------------------------

  toggle() {
    if (this.audioTarget.paused) {
      if (this.loadFailed) return this.#beginLoad()
      this.#play(this.loadGeneration)
    } else {
      this.audioTarget.pause()
    }
  }

  // Scrubbing ---------------------------------------------------------------------------

  // A press on the bar is a seek, and holding it is a drag: the pointer is captured, so the
  // playhead keeps following it past the ends of the bar and the release lands here rather
  // than on whatever the cursor happens to be over by then.
  scrubStart(event) {
    event.preventDefault();
    this.trackTarget.setPointerCapture?.(event.pointerId);
    this.scrubbing = true;
    this.trackTarget.focus();
    this.#seekTo(event.clientX);
  }

  scrubMove(event) {
    if (this.scrubbing) this.#seekTo(event.clientX);
  }

  scrubEnd(event) {
    if (!this.scrubbing) return;

    this.scrubbing = false;
    this.trackTarget.releasePointerCapture?.(event.pointerId);
  }

  // A slider answers a slider's keys. Space is the exception the ARIA pattern allows here:
  // this bar is a transport as much as it is a position, and the button beside it is the only
  // other way to pause once the focus has landed on the bar.
  key(event) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      return this.toggle();
    }

    const moves = {
      ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5,
      Home: -this.#length(), End: this.#length()
    };
    if (!(event.key in moves)) return;

    event.preventDefault();
    this.#moveTo(this.#position() + moves[event.key]);
  }

  #seekTo(clientX) {
    const track = this.trackTarget.getBoundingClientRect();
    if (!track.width) return;

    this.#moveTo(((clientX - track.left) / track.width) * this.#length());
  }

  #moveTo(seconds) {
    const audio = this.audioTarget;
    const at = Math.min(Math.max(seconds, 0), this.#length());

    // Nothing loaded means no timeline to move along, and a currentTime set on an element in
    // that state is dropped on the floor - which is the state the bar is in before its first
    // clip. Nothing to seek in that case anyway.
    if (audio.readyState === HTMLMediaElement.HAVE_NOTHING) return;

    audio.currentTime = at;
    this.#render();
    this.#broadcast();
  }

  // Putting the bar away also stops the sound - a bar that is gone while a song keeps playing
  // leaves nothing to press.
  close() {
    this.audioTarget.pause()
    this.#cancelLoad()
    this.audioTarget.currentTime = 0
    this.element.classList.add("hidden")
    this.token = null
    this.source = null
    this.#broadcast()
    this.dispatch("dismissed", { target: window })
  }

  // Playback ---------------------------------------------------------------------------------

  changed() {
    const audio = this.audioTarget

    // Back to the start once the clip is over, rather than a full bar reading 0:30 / 0:30 that
    // the next press would have to rewind anyway. The `!paused` half is for a file longer than
    // the window we promise (one cut before #122, or replaced under us): it would run past 0:30
    // on its own, so the window is what ends it.
    if (audio.ended || (!audio.paused && this.#position() >= this.#length())) {
      audio.pause()
      audio.currentTime = 0
      return
    }

    if (!audio.paused) claim(audio)
    this.#render()
    this.#broadcast()
  }

  #beginLoad() {
    const audio = this.audioTarget
    this.#cancelMediaListeners()
    this.loadFailed = false
    this.loadGeneration = this.loadState.start(this.source.src)
    const generation = this.loadGeneration

    const currentSource = () => audio.currentSrc || audio.src
    const ready = () => this.loadState.ready(generation, currentSource())
    const failed = () => this.loadState.fail(generation, currentSource(), "error")
    audio.addEventListener("canplay", ready)
    audio.addEventListener("playing", ready)
    audio.addEventListener("error", failed)
    this.removeMediaListeners = () => {
      audio.removeEventListener("canplay", ready)
      audio.removeEventListener("playing", ready)
      audio.removeEventListener("error", failed)
    }

    // Reassigning and loading explicitly aborts the previous network/decode operation. It also
    // makes retry start a fresh request after a native media error.
    audio.src = this.source.src
    audio.load()
    this.#play(generation)
  }

  #play(generation) {
    const audio = this.audioTarget

    if (this.#position() >= this.#length()) audio.currentTime = 0

    // Starting or resuming from either a card or this transport makes the player the selected
    // sticky surface immediately. Waiting for a later scroll would leave it hidden by the navbar.
    this.dispatch("presented", { target: window })

    // Autoplay policy: nothing here starts without a press any more, so the browser should
    // never refuse - but a rejected promise is an uncaught error rather than a quiet no, and
    // the bar is also driven by the keyboard and by a card the pointer never touched. It stays
    // up with its play button showing, and pressing that is unambiguously the gesture.
    audio.play().catch((error) => {
      // A deliberate pause or source replacement can interrupt a still-pending play promise.
      // The native pause/source events already render the correct state; this is not a failure.
      if (error.name === "AbortError") return

      console.warn("[now-playing] the browser would not start this clip", error)
      if (generation) {
        this.loadState.fail(generation, audio.currentSrc || audio.src, "autoplay")
      }
      this.#render()
      this.#broadcast()
    })
  }

  #cancelLoad() {
    this.loadState?.cancel()
    this.spinnerDelay?.finish()
    this.#cancelMediaListeners()
    this.element.removeAttribute("aria-busy")
    if (this.hasStatusTarget) this.statusTarget.classList.add("hidden")
  }

  #cancelMediaListeners() {
    this.removeMediaListeners?.()
    this.removeMediaListeners = null
  }

  #renderLoading() {
    this.loadFailed = false
    this.element.setAttribute("aria-busy", "true")
    this.statusTextTarget.textContent = this.loadingLabelValue
    this.statusTarget.classList.remove("hidden")
    this.spinnerDelay.start()
    this.#render()
    this.#broadcast()
  }

  #renderReady() {
    this.loadFailed = false
    this.spinnerDelay.finish()
    this.statusTarget.classList.add("hidden")
    this.element.removeAttribute("aria-busy")
    this.#render()
    this.#broadcast()
  }

  #renderFailure(reason) {
    this.loadFailed = true
    this.spinnerDelay.finish()
    this.element.removeAttribute("aria-busy")
    const labels = {
      autoplay: this.autoplayLabelValue,
      timeout: this.timeoutLabelValue,
      error: this.errorLabelValue
    }
    this.statusTextTarget.textContent = labels[reason] || this.errorLabelValue
    this.statusTarget.classList.remove("hidden")
    this.audioTarget.pause()
    this.#render()
    this.#broadcast()
  }

  #render() {
    const playing = !this.audioTarget.paused
    const at = this.#position()
    const length = this.#length()
    const readout = `${clock(at)} / ${clock(length)}`

    this.fillTarget.style.width = `${length ? (at / length) * 100 : 0}%`
    this.timeTarget.textContent = readout
    this.playIconTarget.classList.toggle("hidden", playing)
    this.pauseIconTarget.classList.toggle("hidden", !playing)
    const idleLabel = this.loadFailed ? this.retryLabelValue : this.playLabelValue
    this.toggleTarget.setAttribute("aria-label", playing ? this.pauseLabelValue : idleLabel)
    this.trackTarget.setAttribute("aria-valuenow", Math.round(at))
    this.trackTarget.setAttribute("aria-valuetext", readout)
  }

  // What the cards need to know: which of them is the one playing, and how far along it is.
  #broadcast() {
    const length = this.#length()

    publish({
      token: this.token,
      playing: !this.audioTarget.paused,
      progress: length ? this.#position() / length : 0
    })
  }

  // A song shorter than the clip is all clip; before the metadata arrives, what we promised.
  #length() {
    const duration = this.audioTarget.duration

    return Number.isFinite(duration) && duration > 0
      ? Math.min(duration, this.clipSecondsValue)
      : this.clipSecondsValue
  }

  #position() {
    return Math.min(this.audioTarget.currentTime, this.#length())
  }
}

function clock(seconds) {
  if (!Number.isFinite(seconds)) return "0:00"

  const total = Math.max(0, Math.floor(seconds))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
}
